import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Users,
  UserCheck,
  MapPin,
  Gift,
  Sparkles,
  QrCode,
  Home,
  Printer,
  Copy,
  CheckCheck
} from "lucide-react";
import { Link } from "react-router-dom";

const rules = [
  {
    id: 1,
    icon: Users,
    title: "Participação Única",
    badge: "Regra 01",
    description:
      "Só é permitida uma (1) participação por número de telefone/WhatsApp.",
    detail:
      "Entradas duplicadas com o mesmo número não serão contabilizadas para a premiação.",
    color: "from-amber-500/20 to-amber-600/5",
    iconColor: "text-amber-400"
  },
  {
    id: 2,
    icon: UserCheck,
    title: "Dados Válidos",
    badge: "Regra 02",
    description:
      "É obrigatório preencher o formulário ao final do quiz com seus dados reais.",
    detail:
      "O contato com o vencedor será feito exclusivamente pelo telefone cadastrado.",
    color: "from-emerald-500/20 to-emerald-600/5",
    iconColor: "text-emerald-400"
  },
  {
    id: 3,
    icon: MapPin,
    title: "Apenas para Clientes no Local",
    badge: "Regra 03",
    description:
      "Esta ação é exclusiva para quem está presencialmente na loja parceira.",
    detail:
      "Participações geradas por links compartilhados em grupos ou redes sociais serão desclassificadas.",
    color: "from-blue-500/20 to-blue-600/5",
    iconColor: "text-blue-400"
  },
  {
    id: 4,
    icon: Gift,
    title: "Resgate do Prêmio",
    badge: "Regra 04",
    description:
      "O vencedor deverá comprovar ser o titular do número cadastrado.",
    detail:
      "O ganhador terá o prazo de 7 dias corridos para retirar a premiação após o contato oficial.",
    color: "from-purple-500/20 to-purple-600/5",
    iconColor: "text-purple-400"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

export default function RegulamentoSorteio() {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/20 selection:text-primary">

      {/* Print-only header */}
      <div className="hidden print:block text-center py-6 border-b border-gray-300">
        <p className="text-sm font-bold tracking-widest uppercase text-gray-500 mb-1">
          Regulamento Oficial
        </p>
        <h1 className="text-2xl font-bold text-gray-900">Sorteio · Expointer 2026</h1>
        <p className="text-xs text-gray-400 mt-1">Juliano Wagner · Consultor Patrimonial</p>
      </div>

      {/* Header */}
      <header className="border-b border-border/60 bg-background/90 backdrop-blur-md sticky top-0 z-40 print:hidden">
        <div className="container max-w-3xl h-16 flex items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="text-gold-gradient font-display text-xl font-bold tracking-tight">
              Juliano Wagner
            </span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-muted-foreground border-l border-border/80 pl-2">
              Consultoria Patrimonial
            </span>
          </Link>

          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 px-3 py-1 rounded-full text-xs font-medium text-primary">
              <Sparkles size={12} className="animate-pulse" />
              Expointer 2026
            </div>
            <Link
              to="/"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-white/5"
              title="Ir para a página principal"
            >
              <Home size={18} />
            </Link>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="container max-w-3xl mx-auto px-4 py-10 sm:py-14">

        {/* Hero title */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 px-4 py-1.5 rounded-full text-xs font-medium text-primary mb-4">
            <ShieldCheck size={13} />
            Regulamento Oficial da Campanha
          </div>

          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-light leading-tight mb-3">
            Regras do{" "}
            <span className="italic text-gold-gradient font-normal">Sorteio</span>
          </h1>

          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Para garantir a segurança da campanha e a qualidade dos leads, leia
            atentamente as 4 regras abaixo antes de participar via QR Code.
          </p>
        </motion.div>

        {/* QR Code hint banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="flex items-center gap-4 bg-primary/10 border border-primary/30 rounded-2xl p-5 mb-10 print:hidden"
        >
          <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
            <QrCode size={28} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground text-sm sm:text-base leading-snug">
              Escaneie o QR Code no display de mesa para participar do quiz
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Após responder as perguntas, preencha seus dados para validar o cupom.
            </p>
          </div>
        </motion.div>

        {/* Rules cards */}
        <motion.div
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          {rules.map((rule) => {
            const Icon = rule.icon;
            return (
              <motion.div
                key={rule.id}
                variants={itemVariants}
                className="relative overflow-hidden bg-card-gradient border border-border/60 rounded-2xl p-5 sm:p-6 flex items-start gap-5 group hover:border-primary/40 transition-all duration-300 shadow-[0_2px_20px_-6px_rgba(0,0,0,0.5)] print:shadow-none print:border print:border-gray-200 print:rounded-xl print:mb-4"
              >
                {/* Subtle bg on hover */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${rule.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
                />

                {/* Icon */}
                <div className="relative shrink-0 w-11 h-11 rounded-xl bg-white/5 border border-border flex items-center justify-center group-hover:border-primary/30 transition-colors duration-300">
                  <Icon size={20} className={rule.iconColor} />
                </div>

                {/* Content */}
                <div className="relative flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary/70">
                      {rule.badge}
                    </span>
                  </div>
                  <h2 className="font-display text-base sm:text-lg font-medium text-foreground mb-1.5 leading-snug print:text-sm">
                    {rule.title}
                  </h2>
                  <p className="text-sm text-foreground/80 leading-relaxed mb-1 print:text-xs">
                    {rule.description}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed print:text-xs">
                    {rule.detail}
                  </p>
                </div>

                {/* Number watermark */}
                <span className="absolute bottom-3 right-4 text-[56px] font-display font-bold text-white/[0.03] leading-none select-none pointer-events-none">
                  {String(rule.id).padStart(2, "0")}
                </span>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Tip box */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="mt-8 bg-background border border-dashed border-border/80 rounded-2xl p-5 flex items-start gap-3 print:border-gray-300 print:mt-6"
        >
          <ShieldCheck size={18} className="text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-foreground mb-0.5 uppercase tracking-wider print:text-[10px]">
              Dica para o display
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed print:text-xs">
              Coloque essas regras em fonte menor, mas legível, logo abaixo do QR Code no
              display de mesa ou cartaz. Use esta página no modo impressão (Ctrl+P / ⌘P)
              para gerar um PDF pronto para layout gráfico.
            </p>
          </div>
        </motion.div>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75 }}
          className="flex flex-col sm:flex-row gap-3 mt-8 print:hidden"
        >
          <button
            id="btn-print-regulamento"
            onClick={handlePrint}
            className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-medium text-sm transition-all shadow-gold hover:translate-y-[-1px]"
            style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
          >
            <Printer size={16} /> Imprimir / Salvar PDF
          </button>

          <button
            id="btn-copy-link-regulamento"
            onClick={handleCopyLink}
            className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-medium text-sm border border-border hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground"
          >
            {copied ? (
              <>
                <CheckCheck size={16} className="text-emerald-400" />
                <span className="text-emerald-400">Link copiado!</span>
              </>
            ) : (
              <>
                <Copy size={16} /> Copiar link do regulamento
              </>
            )}
          </button>

          <Link
            to="/expointer"
            id="btn-go-quiz"
            className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-medium text-sm border border-primary/40 hover:bg-primary/10 transition-colors text-primary"
          >
            <QrCode size={16} /> Participar do quiz
          </Link>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 py-4 px-4 text-center text-xs text-muted-foreground print:hidden">
        <div className="container max-w-3xl flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>Juliano Wagner · Consultor Patrimonial ANCORD & Ademicon</span>
          <span>Expointer 2026 · Regulamento da Campanha</span>
        </div>
      </footer>
    </div>
  );
}
